import { DOC_TYPES } from '@api/lendingAPI/ProductOfferAPIHelper';
import { ArrowRightSVG } from '@svgComponents/arrowRightSVG';
import { CircleSVG } from '@svgComponents/CircleSVG';
import { FailedSVG } from '@svgComponents/failedSVG/index';
import { TickCircleSVG } from '@svgComponents/tickCircleSVG';
import classnames from 'classnames';

import styles from './DocumentRequirements.module.css';
import { TDocumentRequirements } from '../../ApplyFlexiLoanFormView';
import LatestBankStatements from '../latestBankStatements/LatestBankStatements';

export const DocumentRequirements = ({
    documentRequirements,
    hasSubmitFailed,
    uploadDocumentRequirement
}: {
    documentRequirements: TDocumentRequirements;
    hasSubmitFailed: boolean;
    uploadDocumentRequirement: (documentType: {
        label: string;
        value: string;
    }) => void;
}) => {
    return (
        <>
            <LatestBankStatements hasSubmitFailed={hasSubmitFailed} />
            {documentRequirements.map(
                ({
                    status,
                    type,
                    hasItems,
                    hasError,
                    quantifier,
                    uploadModalDocType
                }: any) => (
                    <div
                        className={styles.documentRequirement}
                        onClick={() =>
                            uploadDocumentRequirement(uploadModalDocType)
                        }
                        data-testid={`${type}-requirement`}
                        key={type}
                        role="button"
                    >
                        <DocumentStatusIndicator
                            status={status}
                            hasItems={hasItems}
                            hasError={hasError}
                        />

                        <div className={styles.contentWrapper}>
                            <span className={styles.documentLabel}>
                                <DocumentRequirementLabel
                                    type={type!}
                                    quantifier={quantifier as number}
                                />
                            </span>
                            <DocumentDescription type={type!} />
                        </div>

                        <ArrowRightSVG className={styles.documentArrow} />

                        {status !== 'pending' &&
                            status !== 'completed' &&
                            hasSubmitFailed && (
                                <div
                                    data-testid="formErrors"
                                    className={styles.error}
                                >
                                    {type === DOC_TYPES.VAT_RETURN && (
                                        <span>
                                            Please upload your VAT returns
                                        </span>
                                    )}
                                    {type === DOC_TYPES.BANK_STATEMENT && (
                                        <span>
                                            Please upload your bank statements
                                        </span>
                                    )}
                                    {type === DOC_TYPES.COMPANY_ACCOUNTS && (
                                        <span>
                                            Please upload your filed company
                                            accounts
                                        </span>
                                    )}
                                    {type === DOC_TYPES.MANAGEMENT_ACCOUNTS && (
                                        <span>
                                            Please upload your management
                                            accounts
                                        </span>
                                    )}
                                    {type === DOC_TYPES.AGED_DEBTOR_REPORT && (
                                        <span>
                                            Please upload your aged debtor
                                            report
                                        </span>
                                    )}
                                    {type ===
                                        DOC_TYPES.OUTSTANDING_DEBT_INFORMATION && (
                                        <span>
                                            Please upload your debt position
                                        </span>
                                    )}
                                    {type === DOC_TYPES.CASHFLOW_FORECAST && (
                                        <span>
                                            Please upload your business forecast
                                        </span>
                                    )}
                                    {type ===
                                        DOC_TYPES.SELF_ASSESSMENT_TAX_RETURN && (
                                        <span>
                                            Please upload your self assessment
                                            tax return
                                        </span>
                                    )}
                                </div>
                            )}
                    </div>
                )
            )}
        </>
    );
};

const DocumentDescription = ({ type }: { type: string }) => {
    switch (type) {
        case DOC_TYPES.MANAGEMENT_ACCOUNTS:
            return (
                <p className={styles.documentCaption}>
                    Please share a breakdown of your balance sheet and profit &
                    loss positions over the last 12 months. It should cover your
                    latest financial year end.
                </p>
            );

        case DOC_TYPES.OUTSTANDING_DEBT_INFORMATION:
            const businessDebtPositionLink =
                'https://static.iwoca.com/download/CBILS/business_debt_position_iwoca_template.xlsx';

            return (
                <p className={styles.documentCaption}>
                    Please tell us your debt position by{' '}
                    <a
                        href={businessDebtPositionLink}
                        rel="noreferrer noopener"
                        data-testid="outstanding-debt-information-link"
                        download
                    >
                        using our template
                    </a>
                    .
                </p>
            );

        case DOC_TYPES.CASHFLOW_FORECAST:
            return (
                <p className={styles.documentCaption}>
                    Please share your forecast of fixed/essential costs and
                    revenues by{' '}
                    <a
                        href="https://static.iwoca.com/download/CBILS/cashflow_forecast_iwoca_template.xlsx"
                        rel="noreferrer noopener"
                        download
                    >
                        using our template
                    </a>
                    .
                </p>
            );

        case DOC_TYPES.VAT_RETURN:
            return null;

        default:
            return null;
    }
};

const DocumentRequirementLabel = ({
    type,
    quantifier
}: {
    type: string;
    quantifier: number;
}) => {
    const quantifierByYearsLabel = `for the last ${
        quantifier === 1 ? 'year' : `${quantifier} years`
    }`;
    switch (type) {
        case DOC_TYPES.BANK_STATEMENT:
            return (
                <>
                    Bank statements{' '}
                    <span className={styles.documentLabelCaption}>
                        for the last {quantifier} months
                    </span>
                </>
            );
        case DOC_TYPES.VAT_RETURN:
            return (
                <>
                    VAT returns{' '}
                    <span className={styles.documentLabelCaption}>
                        for the last {quantifier} months
                    </span>
                </>
            );
        case DOC_TYPES.COMPANY_ACCOUNTS:
            return (
                <>
                    Filed company accounts{' '}
                    <span className={styles.documentLabelCaption}>
                        {quantifierByYearsLabel}
                    </span>
                </>
            );
        case DOC_TYPES.MANAGEMENT_ACCOUNTS:
            return (
                <>
                    Management accounts{' '}
                    <span className={styles.documentLabelCaption}>
                        {quantifierByYearsLabel}
                    </span>
                </>
            );
        case DOC_TYPES.AGED_DEBTOR_REPORT:
            return <>Aged Debtor Report</>;
        case DOC_TYPES.OUTSTANDING_DEBT_INFORMATION:
            return <>Debt position</>;

        case DOC_TYPES.CASHFLOW_FORECAST:
            return (
                <>
                    Cashflow forecast{' '}
                    <span className={styles.documentLabelCaption}>
                        for the next 3 months
                    </span>
                </>
            );
        case DOC_TYPES.SELF_ASSESSMENT_TAX_RETURN:
            return <>Self assessment tax return</>;

        default:
            return null;
    }
};

const DocumentStatusIndicator = ({
    status,
    hasItems,
    hasError
}: {
    status: string;
    hasItems: boolean;
    hasError: boolean;
}) => {
    if (hasError)
        return (
            <FailedSVG
                className={classnames(styles.statusIcon, styles.failedIcon)}
            />
        );
    if (hasItems || status === 'completed')
        return <TickCircleSVG className={styles.statusIcon} />;

    return <CircleSVG className={styles.statusIcon} />;
};
